import React from 'react';
import { UserIcon, EnvelopeIcon, PhoneIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import type { Usuario, UsuariosResponse } from '../../types/usuarios';
import { formatDateTime, maskTelefoneInput } from '../../utils/format';

interface UsuariosListProps {
  usuariosData?: UsuariosResponse;
  onEdit: (usuario: Usuario) => void;
  isLoading: boolean;
  currentPage: number;
  onPageChange: (page: number) => void;
}

/** Iniciais pro avatar (até 2 letras do nome). */
function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/);
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

/** "há 2 dias" / "agora" — último acesso relativo (Íris). */
function tempoRelativo(iso?: string): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '—';
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 30) return d === 1 ? 'ontem' : `há ${d} dias`;
  const m = Math.floor(d / 30);
  if (m < 12) return `há ${m} ${m === 1 ? 'mês' : 'meses'}`;
  const a = Math.floor(m / 12);
  return `há ${a} ${a === 1 ? 'ano' : 'anos'}`;
}

const BadgePapel: React.FC<{ perfil?: string }> = ({ perfil }) => (
  <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-iris-sm ${
    perfil === 'ADMIN' ? 'bg-accent-soft text-accent-text' : 'bg-surface-2 text-tx-2'
  }`}>
    {perfil === 'ADMIN' ? 'Admin' : 'Usuário'}
  </span>
);

const BadgeStatus: React.FC<{ ativo: boolean }> = ({ ativo }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-iris-sm ${
    ativo ? 'bg-ok-soft text-ok' : 'bg-surface-2 text-tx-3'
  }`}>
    <span className={`h-1.5 w-1.5 rounded-full ${ativo ? 'bg-ok' : 'bg-tx-3'}`} />
    {ativo ? 'Ativo' : 'Inativo'}
  </span>
);

const UsuariosList: React.FC<UsuariosListProps> = ({
  usuariosData,
  onEdit,
  isLoading,
  currentPage,
  onPageChange,
}) => {
  if (isLoading) {
    return (
      <div className="bg-surface border border-line rounded-iris-lg p-8 text-center">
        <div className="animate-spin h-8 w-8 border-4 border-accent border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-tx-2">Carregando usuários...</p>
      </div>
    );
  }

  if (!usuariosData?.data || usuariosData.data.length === 0) {
    return (
      <div className="bg-surface border border-line rounded-iris-lg p-8 text-center">
        <UserIcon className="h-12 w-12 text-tx-3 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-tx mb-1">Nenhum usuário encontrado</h3>
        <p className="text-sm text-tx-2">Não há usuários com os filtros aplicados.</p>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-line rounded-iris-lg overflow-hidden">
      {/* Header desktop */}
      <div className="hidden lg:grid grid-cols-12 gap-3 px-4 py-2.5 bg-surface-2 border-b border-line text-[11px] font-semibold uppercase tracking-wide text-tx-3">
        <div className="col-span-3">Usuário</div>
        <div className="col-span-2">Login</div>
        <div className="col-span-3">Email</div>
        <div className="col-span-1">Papel</div>
        <div className="col-span-1">Último acesso</div>
        <div className="col-span-1">Status</div>
        <div className="col-span-1" />
      </div>

      <div className="divide-y divide-line">
        {usuariosData.data.map((usuario) => (
          <button
            key={usuario.id}
            type="button"
            onClick={() => onEdit(usuario)}
            className="block w-full text-left p-4 lg:px-4 lg:py-2.5 hover:bg-surface-2 transition-colors"
          >
            {/* Mobile */}
            <div className="lg:hidden space-y-2">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-soft text-accent-text text-xs font-semibold">
                  {iniciais(usuario.nome)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-tx">{usuario.nome}</span>
                    <BadgePapel perfil={usuario.perfil} />
                    <BadgeStatus ativo={usuario.ativo} />
                  </div>
                  <div className="mt-1 space-y-0.5 text-[13px] text-tx-2">
                    <div className="flex items-center gap-1.5"><UserIcon className="h-3.5 w-3.5" />{usuario.login}</div>
                    <div className="flex items-center gap-1.5 truncate"><EnvelopeIcon className="h-3.5 w-3.5" />{usuario.email}</div>
                    {usuario.fone && (
                      <div className="flex items-center gap-1.5"><PhoneIcon className="h-3.5 w-3.5" />{maskTelefoneInput(usuario.fone)}</div>
                    )}
                    <div className="text-tx-3">Último acesso: {tempoRelativo(usuario.ultimoLogin)}</div>
                  </div>
                </div>
                <ChevronRightIcon className="h-4 w-4 text-tx-3 shrink-0" />
              </div>
            </div>

            {/* Desktop */}
            <div className="hidden lg:grid grid-cols-12 gap-3 items-center">
              <div className="col-span-3 flex items-center gap-2.5 min-w-0">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent-soft text-accent-text text-[11px] font-semibold">
                  {iniciais(usuario.nome)}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-tx truncate">{usuario.nome}</p>
                  {usuario.fone && <p className="text-xs text-tx-3 truncate">{maskTelefoneInput(usuario.fone)}</p>}
                </div>
              </div>
              <div className="col-span-2 text-[13px] font-mono text-tx truncate">{usuario.login}</div>
              <div className="col-span-3 text-[13px] text-tx-2 truncate">{usuario.email}</div>
              <div className="col-span-1"><BadgePapel perfil={usuario.perfil} /></div>
              <div className="col-span-1 text-xs text-tx-2" title={formatDateTime(usuario.ultimoLogin, 'Nunca')}>
                {tempoRelativo(usuario.ultimoLogin)}
              </div>
              <div className="col-span-1"><BadgeStatus ativo={usuario.ativo} /></div>
              <div className="col-span-1 flex justify-end">
                <ChevronRightIcon className="h-4 w-4 text-tx-3" />
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Paginação */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-3 px-4 py-3 border-t border-line">
        <div className="text-[13px] text-tx-2">
          Mostrando {((currentPage - 1) * usuariosData.pageSize) + 1} a {Math.min(currentPage * usuariosData.pageSize, usuariosData.totalCount)} de {usuariosData.totalCount} usuários
        </div>
        {usuariosData.totalPages > 1 && (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Página anterior"
              className="p-1.5 border border-line rounded-iris-sm text-tx-2 hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <span className="px-2.5 py-1 bg-accent-soft text-accent-text rounded-iris-sm text-[13px] font-semibold">
              {currentPage} / {usuariosData.totalPages}
            </span>
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === usuariosData.totalPages}
              aria-label="Próxima página"
              className="p-1.5 border border-line rounded-iris-sm text-tx-2 hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsuariosList;
